import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, X, ChevronRight, Sparkles } from "lucide-react";

// Detección de domingo:
// - Todo el domingo (0 = Sunday en JS): banner con recetas largas
// - Domingo antes de las 11am: hero grande "hoy toca lucirte"
//
// El usuario puede descartar el hero por el resto del día (localStorage).

const ESPECIAL_KEYWORDS = /asado|guiso|larg|braseado|tradicional|festivo|fin de semana|domingo|celebra|osso ?buco|cazuela|pastel|carbonada|pernil|risotto|estofad|osobuco|cocido/i;
const LONG_CATEGORIES = new Set([
  "Carnes y Pollos",
  "Festividades Judías",
  "Platos Internacionales",
]);
const EXCLUDE_CATEGORIES = new Set([
  "Bebidas y Cocktails",
  "Salsas, Aderezos y Básicos",
  "Panes, Empanadas y Fritos",
  "Postres y Tortas",
  "Desayunos y Brunch",
]);

function scoreLongRecipe(r) {
  const text = `${r.nombre || ""} ${r.categoria || ""} ${(r.tags || []).join(" ")} ${r.descripcion_corta || ""} ${r.tiempo_coccion || ""}`;
  if (EXCLUDE_CATEGORIES.has(r.categoria)) return -100;
  let s = 0;
  if (ESPECIAL_KEYWORDS.test(text)) s += 4;
  if (LONG_CATEGORIES.has(r.categoria)) s += 2;
  // Tiempo largo (busca "hora" en tiempo_preparacion o tiempo_coccion)
  const timeText = `${r.tiempo_preparacion || ""} ${r.tiempo_coccion || ""}`;
  if (/hora|hr/i.test(timeText)) s += 2;
  return s;
}

function pickLongRecipes(all, count) {
  const scored = (all || [])
    .map((r) => ({ r, s: scoreLongRecipe(r) + Math.random() * 0.7 }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s);
  const chosen = [];
  const usedCats = new Set();
  for (const { r } of scored) {
    if (chosen.length >= count) break;
    // Varía categorías en las primeras posiciones
    if (chosen.length < 3 && usedCats.has(r.categoria)) continue;
    chosen.push(r);
    usedCats.add(r.categoria);
  }
  // Rellenar si faltan
  for (const { r } of scored) {
    if (chosen.length >= count) break;
    if (!chosen.some((x) => x.slug === r.slug)) chosen.push(r);
  }
  return chosen;
}

// Un domingo "de hoy" identificable — para persistir dismissals
function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export default function SundayMode({ recipes }) {
  const [heroDismissed, setHeroDismissed] = useState(() => {
    try { return localStorage.getItem("recetario_sunday_hero_dismissed") === todayKey(); } catch { return false; }
  });
  const now = new Date();
  const isSunday = now.getDay() === 0;
  const isMorning = isSunday && now.getHours() < 11;

  const chosen = useMemo(() => (recipes && recipes.length ? pickLongRecipes(recipes, 6) : []), [recipes]);

  // Re-evaluar en caso de tab quedar abierto muchas horas
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isSunday) return;
    const t = setInterval(() => setTick((n) => n + 1), 30 * 60 * 1000); // cada 30 min
    return () => clearInterval(t);
  }, [isSunday]);
  void tick;

  if (!isSunday || chosen.length === 0) return null;

  const hero = chosen[0];
  const rest = chosen.slice(1);

  const dismissHero = () => {
    setHeroDismissed(true);
    try { localStorage.setItem("recetario_sunday_hero_dismissed", todayKey()); } catch { /* silent */ }
  };

  return (
    <section data-testid="sunday-mode" className="mb-8">
      {/* Banner destacado (todo el día) */}
      <div className="rounded-2xl border-2 border-orange-500/40 bg-gradient-to-br from-orange-100 via-amber-light/50 to-paper-warm p-5 mb-4 relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-32 h-32 bg-orange-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="flex items-start gap-3 mb-1 relative">
          <div className="w-11 h-11 rounded-full bg-orange-500/25 flex items-center justify-center shrink-0">
            <Sun className="w-6 h-6 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-[10px] text-orange-700">Domingo en casa</p>
            <h2 className="font-display italic text-cocoa text-2xl leading-tight">Modo Domingo</h2>
            <p className="font-serif text-cocoa-soft text-sm italic mt-1">
              Día de olla lenta, sobremesa y cabros a la mesa. Estas son las recetas para lucirte.
            </p>
          </div>
        </div>
      </div>

      {/* Hero — solo en la mañana del domingo, antes de las 11 */}
      {isMorning && !heroDismissed && hero && (
        <div className="rounded-2xl border-2 border-amber-deep/50 bg-cream overflow-hidden mb-4 relative shadow-lg">
          <button
            data-testid="sunday-hero-close"
            onClick={dismissHero}
            className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-paper/90 border border-line hover:bg-cream"
            aria-label="Cerrar"
          ><X className="w-4 h-4 text-cocoa" /></button>
          <Link to={`/receta/${hero.slug}`} data-testid="sunday-hero-link" className="block">
            {hero.photo ? (
              <img src={hero.photo} alt={hero.nombre} className="w-full h-48 sm:h-64 object-cover" />
            ) : (
              <div className="w-full h-48 sm:h-64 bg-gradient-to-br from-amber-light to-paper-warm" />
            )}
            <div className="p-5">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-amber-deep" />
                <p className="eyebrow text-[10px] text-amber-deep">Hoy toca lucirte</p>
              </div>
              <h3 className="font-display italic text-cocoa text-3xl leading-tight mb-2">{hero.nombre}</h3>
              {hero.descripcion_corta && (
                <p className="font-serif text-cocoa-soft italic line-clamp-2 mb-3">{hero.descripcion_corta}</p>
              )}
              <span className="btn-primary inline-flex">
                Cocinar hoy <ChevronRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </div>
      )}

      {/* Grid con las otras sugerencias */}
      <div>
        <p className="eyebrow mb-3">Más ideas para el domingo</p>
        <ul data-testid="sunday-list" className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {(isMorning && !heroDismissed ? rest : chosen).slice(0, 6).map((r) => (
            <li key={r.slug}>
              <Link
                data-testid={`sunday-item-${r.slug}`}
                to={`/receta/${r.slug}`}
                className="block bg-cream border border-line rounded-xl overflow-hidden hover:border-amber-deep transition"
              >
                {r.photo ? (
                  <img src={r.photo} alt={r.nombre} className="w-full h-24 sm:h-28 object-cover" />
                ) : (
                  <div className="w-full h-24 sm:h-28 bg-paper-warm" />
                )}
                <div className="p-2.5">
                  <p className="eyebrow text-[9px] line-clamp-1">{r.categoria}</p>
                  <h4 className="font-serif text-cocoa text-sm font-semibold leading-tight line-clamp-2">{r.nombre}</h4>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
